import { ReactNode } from 'react'

export const UNMOUNTED = 'unmounted'
export const EXITED = 'exited'
export const ENTERING = 'entering'
export const ENTERED = 'entered'
export const EXITING = 'exiting'

export type TransitionStatus =
  | typeof ENTERING
  | typeof ENTERED
  | typeof EXITING
  | typeof EXITED
  | typeof UNMOUNTED

export type TransitionChildren =
  | ReactNode
  | ((
      status: TransitionStatus,
      childProps?: Record<string, unknown>
    ) => ReactNode)

type RefHandler<
  RefElement extends undefined | HTMLElement,
  ImplicitRefHandler extends (node: HTMLElement, ...args: any[]) => void,
  ExplicitRefHandler extends (...args: any[]) => void,
> = {
  implicit: ImplicitRefHandler
  explicit: ExplicitRefHandler
}[RefElement extends undefined ? 'implicit' : 'explicit']

export type EndHandler<RefElement extends undefined | HTMLElement> = RefHandler<
  RefElement,
  (node: HTMLElement, done: () => void) => void,
  (done: () => void) => void
>

export type EnterHandler<RefElement extends undefined | HTMLElement> =
  RefHandler<
    RefElement,
    (node: HTMLElement, isAppearing: boolean) => void,
    (isAppearing: boolean) => void
  >

export type ExitHandler<E extends undefined | HTMLElement> = RefHandler<
  E,
  (node: HTMLElement) => void,
  () => void
>

export interface TransitionActions {
  /**
   * Normally a component is not transitioned if it is shown when the
   * `<Transition>` component mounts. If you want to transition on the first
   * mount set  appear to true, and the component will transition in as soon
   * as the `<Transition>` mounts. Note: there are no specific "appear" states.
   * appear only adds an additional enter transition.
   */
  appear?: boolean
  /**
   * Enable or disable enter transitions.
   */
  enter?: boolean
  /**
   * Enable or disable exit transitions.
   */
  exit?: boolean
}

interface BaseTransitionProps<RefElement extends undefined | HTMLElement>
  extends TransitionActions {
  /**
   * Show the component; triggers the enter or exit states
   */
  in?: boolean
  /**
   * By default the child component is mounted immediately along with the
   * parent Transition component. If you want to "lazy mount" the component on
   * the first `in={true}` you can set `mountOnEnter`. After the first enter
   * transition the component will stay mounted, even on "exited", unless you
   * also specify `unmountOnExit`.
   */
  mountOnEnter?: boolean
  /**
   * By default the child component stays mounted after it reaches the
   * 'exited' state. Set `unmountOnExit` if you'd prefer to unmount the
   * component after it finishes exiting.
   */
  unmountOnExit?: boolean
  /**
   * Callback fired before the "entering" status is applied. An extra
   * parameter `isAppearing` is supplied to indicate if the enter stage is
   * occurring on the initial mount
   */
  onEnter?: EnterHandler<RefElement>
  /**
   * Callback fired after the "entering" status is applied. An extra parameter
   * isAppearing is supplied to indicate if the enter stage is occurring on
   * the initial mount
   */
  onEntering?: EnterHandler<RefElement>
  /**
   * Callback fired after the "entered" status is applied. An extra parameter
   * isAppearing is supplied to indicate if the enter stage is occurring on
   * the initial mount
   */
  onEntered?: EnterHandler<RefElement>
  /**
   * Callback fired before the "exiting" status is applied.
   */
  onExit?: ExitHandler<RefElement>
  /**
   * Callback fired after the "exiting" status is applied.
   */
  onExiting?: ExitHandler<RefElement>
  /**
   * Callback fired after the "exited" status is applied.
   */
  onExited?: ExitHandler<RefElement>
  /**
   * A function child can be used instead of a React element. This function is
   * called with the current transition status ('entering', 'entered',
   * 'exiting',  'exited', 'unmounted'), which can be used to apply context
   * specific props to a component.
   */
  children?: TransitionChildren
  /**
   * A React reference to DOM element that need to transition
   * When `nodeRef` prop is used, node is not passed to callback functions
   * (e.g. onEnter) because user already has direct access to the node.
   */
  nodeRef?: React.Ref<RefElement>

  [prop: string]: any
}

export interface TimeoutProps<RefElement extends undefined | HTMLElement>
  extends BaseTransitionProps<RefElement> {
  /**
   * The duration of the transition, in milliseconds.
   * Required unless addEndListener is provided.
   */
  timeout: number | { appear?: number; enter?: number; exit?: number }
  /**
   * Add a custom transition end trigger. Called with the transitioning DOM
   * node and a done callback. Allows for more fine grained transition end
   * logic. Note: Timeouts are still used as a fallback if provided.
   */
  addEndListener?: EndHandler<RefElement>
}

export interface EndListenerProps<Ref extends undefined | HTMLElement>
  extends BaseTransitionProps<Ref> {
  /**
   * The duration of the transition, in milliseconds.
   * Required unless addEndListener is provided.
   */
  timeout?: number | { appear?: number; enter?: number; exit?: number }
  /**
   * Add a custom transition end trigger. Called with the transitioning DOM
   * node and a done callback. Allows for more fine grained transition end
   * logic. Note: Timeouts are still used as a fallback if provided.
   */
  addEndListener: EndHandler<Ref>
}

export type TransitionProps<
  RefElement extends undefined | HTMLElement = undefined,
> = TimeoutProps<RefElement> | EndListenerProps<RefElement>

export interface CSSTransitionClassNames {
  appear?: string
  appearActive?: string
  appearDone?: string
  enter?: string
  enterActive?: string
  enterDone?: string
  exit?: string
  exitActive?: string
  exitDone?: string
}

export type CSSTransitionProps<
  Ref extends undefined | HTMLElement = undefined,
> = TransitionProps<Ref> & {
  /**
   * The animation `classNames` applied to the component as it enters or exits.
   * A single name can be provided and it will be suffixed for each stage.
   *
   * Each individual classNames can also be specified independently.
   */
  classNames?: string | CSSTransitionClassNames
}
